/**
 * Copyright 2025 Lumavec Group LLC
 *
 * Worktree status info component - shows branch, commit, and collapsible message
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { WorktreeStatus } from './types';

interface WorktreeStatusInfoProps {
  status: WorktreeStatus | null;
}

export function WorktreeStatusInfo({ status }: WorktreeStatusInfoProps) {
  const [isMessageExpanded, setIsMessageExpanded] = useState(false);
  
  const commitMessage = status?.commitMessage || 'No commit message';
  const hasMessage = status?.commitMessage && status.commitMessage.length > 0;

  return (
    <div className="p-3 bg-gray-50 changes-page-panel-header">
      <div className="text-sm">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-600">Commit:</span>
          <code className="text-xs bg-gray-200 px-1.5 py-0.5 rounded font-mono">
            {status?.commit?.substring(0, 7) || '—'}
          </code>
        </div>
        
        {hasMessage && (
          <button
            onClick={() => setIsMessageExpanded(!isMessageExpanded)}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 mt-1.5 w-full text-left"
          >
            {isMessageExpanded ? (
              <ChevronDown size={12} className="flex-shrink-0" />
            ) : (
              <ChevronRight size={12} className="flex-shrink-0" />
            )}
            <span className={isMessageExpanded ? '' : 'truncate'}>
              {isMessageExpanded ? 'Hide message' : commitMessage}
            </span>
          </button>
        )}
        
        {isMessageExpanded && hasMessage && (
          <div className="text-xs text-gray-600 mt-1.5 pl-4 whitespace-pre-wrap break-words">
            {commitMessage}
          </div>
        )}
      </div>
    </div>
  );
}
